import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Loader from "@amzn/meridian/loader";
import _ from "lodash";
import React, { lazy, Suspense, useEffect, useState, useTransition } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../app/store/hooks";
import {
    useCreateRealPropertyMutation,
    useUpdateRealPropertyMutation,
} from "../../endpoints/createRealProperty";
import { useGetRealPropertyDetailsQuery } from "../../endpoints/getRealProperty";
import ApiIndicator from "../../features/api-indicator/ApiIndicator";
import { useNotifications } from "../../features/notifications/hooks";
import { parseJSON } from "../../utils/helperUtils";
import {
    createPropertyFormSteps,
    createPropertyInitialValues,
    featuresQuantityUnitMap,
    mapKeysToUnitKeys,
    ResponsiveFormConfig,
    responsiveSuccessToastHandler,
    successPropertyCreated,
    successUpdatedToast,
    siteImprovementDetailsEditInitialValues,
} from "../create-property/definitions";
import { propertyDetailKeys } from "../create-property/utils";
import { Space } from "../space/Space";
import { omitKeysFromArray } from "./utils";

const LazyCreateEditPropertyForm = lazy(
    () => import("../create-property/ResponsiveProgressTrackerForm")
);

export function delayValueFromReturning(promise) {
    return new Promise(resolve => {
        setTimeout(resolve, 2000);
    }).then(() => {
        return promise;
    });
}

export const AddEditRealProperty: React.FC<any> = () => {
    const [initialValues, setInitialValues] = useState(null);
    const { propertyId } = useParams();
    const { pathname } = useLocation();
    const [onCreateProperty] = useState(pathname.split("/").splice(-1)[0] === "create-property");
    const [onEditProperty] = useState(pathname.split("/").splice(-1)[0] === "edit-property");
    const [siteCode, setSiteCode] = useState("");

    const { data: realPropertyData, isSuccess: getRealPropertyIsSuccess } =
        useGetRealPropertyDetailsQuery(
            {
                artifactId: propertyId,
                businessUseCase: "SMART",
            },
            { skip: onCreateProperty }
        );

    const [
        createRealPropertyTrigger,
        {
            error: createError,
            isLoading: createIsLoading,
            isSuccess: createIsSuccess,
            isError: createIsError,
        },
    ] = useCreateRealPropertyMutation();

    const [
        updateRealPropertyTrigger,
        {
            error: updateError,
            isLoading: updateIsLoading,
            isSuccess: updateIsSuccess,
            isError: updateIsError,
        },
    ] = useUpdateRealPropertyMutation();

    const [isPending, startTransition] = useTransition();
    useEffect(() => {
        if (!onCreateProperty && getRealPropertyIsSuccess) {
            const parsedData = parseJSON(realPropertyData);
            const key = propertyDetailKeys;

            const result = {
                propertyId: _.pick(parsedData.propertyDetails, key.propertyId),
                ..._.pick(parsedData.propertyDetails, key.address),
                basicInformation: _.pick(parsedData.propertyDetails, key.basicInformation),
                statusAndOwnership: _.pick(parsedData.propertyDetails, key.statusAndOwnership),
                otherInformation: _.pick(parsedData.propertyDetails, key.otherInformation),
                financialInformation: _.pick(parsedData.propertyDetails, key.financialInformation),
                siteImprovementDetails: Array.from(
                    new Map([
                        ...siteImprovementDetailsEditInitialValues.map(improvement => [
                            improvement?.siteImprovementType + improvement?.siteImprovementName,
                            improvement,
                        ]),
                        ...parsedData.siteImprovementDetails.map(improvement => [
                            improvement?.siteImprovementType + improvement?.siteImprovementName,
                            improvement,
                        ]),
                    ]).values()
                ),

                spaceDetails: {
                    addedSpaces: parsedData?.spaceDetails.map(space => ({
                        id: _.uniqueId(),
                        ...space,
                    })),
                },
                featureDetails: {
                    addedFeatures: parsedData?.featureDetails.map(feature => ({
                        id: _.uniqueId(),
                        ...feature,
                    })),
                },
            };
            startTransition(() => {
                setInitialValues(result);
            });
        }
    }, [realPropertyData, getRealPropertyIsSuccess]);
    const { alias } = useAppSelector(state => state.auth.user);

    const { addToast } = useNotifications();

    const navigate = useNavigate();

    useEffect(() => {
        if (createIsSuccess) {
            successPropertyCreated(siteCode, addToast);
            navigate(-1);
        }
    }, [createIsSuccess]);

    useEffect(() => {
        if (updateIsSuccess) {
            successUpdatedToast(siteCode, addToast);
            navigate(-1);
        }
    }, [updateIsSuccess]);

    const handleSubmit = async valuesToSubmit => {
        const formValues = valuesToSubmit[0];
        setSiteCode(formValues.propertyId.siteCode);

        const formData = {
            clientName: alias,
            businessUseCase: "SMART",
            propertyDetails: {
                ...realPropertyData?.propertyDetails,
                ...formValues.propertyId,
                address: formValues.address,
                ...formValues.basicInformation,
                ...formValues.statusAndOwnership,
                ...formValues.otherInformation,
                ...formValues.financialInformation,
            },
            featureDetails: formValues.featureDetails?.addedFeatures.map(feature => {
                Object.entries(feature).forEach(([, featureValue]: [string, any]) => {
                    const featureExists = featuresQuantityUnitMap.has(featureValue);
                    const isNotOtherType = feature.featureType !== "Other";
                    const featureName = featuresQuantityUnitMap.get(featureValue)?.featureName;
                    const quantityUnit = featuresQuantityUnitMap.get(featureValue)?.quantityUnit;
                    featureExists && isNotOtherType
                        ? _.set(feature, "featureName", featureName)
                        : null;
                    featureExists ? _.set(feature, "quantityUnit", quantityUnit) : null;
                });

                return _.omit(feature, ["id"]);
            }),
            siteImprovementDetails: formValues.siteImprovementDetails,
            spaceDetails: formValues?.spaceDetails.addedSpaces?.map(space => {
                Object.entries(space).forEach(([key]) => {
                    mapKeysToUnitKeys.has(key)
                        ? _.set(space, `${key}Unit`, mapKeysToUnitKeys.get(key).value)
                        : null;
                });

                return _.omit(space, ["id"]);
            }),
        };

        const props = formData.propertyDetails;

        if (onEditProperty) {
            await updateRealPropertyTrigger({
                clientName: alias,
                businessUseCase: "SMART",
                propertyDetails: props,
                artifactId: propertyId,
                featureDetails: {
                    created: omitKeysFromArray(
                        formData.featureDetails.filter(feature => !feature.artifactId),
                        ["id"]
                    ),
                    modified: omitKeysFromArray(
                        formData.featureDetails.filter(feature => !!feature.artifactId),
                        ["id"]
                    ),
                    deleted: omitKeysFromArray(
                        initialValues.featureDetails.addedFeatures.filter(
                            feature =>
                                !formData.featureDetails
                                    .map(values => values.artifactId)
                                    .includes(feature.artifactId)
                        ),
                        ["id"]
                    ),
                },
                siteImprovementDetails: {
                    created: omitKeysFromArray(
                        formData.siteImprovementDetails.filter(space => !space.artifactId),
                        ["id"]
                    ),
                    modified: omitKeysFromArray(
                        formData.siteImprovementDetails.filter(space => !!space.artifactId),
                        ["id"]
                    ),
                    deleted: omitKeysFromArray(
                        initialValues.siteImprovementDetails.filter(
                            improvement =>
                                !formData.siteImprovementDetails
                                    .map(values => values.artifactId)
                                    .includes(improvement.artifactId)
                        ),
                        ["id"]
                    ),
                },
                spaceDetails: {
                    created: omitKeysFromArray(
                        formData.spaceDetails.filter(space => !space.artifactId),
                        ["id"]
                    ),
                    modified: omitKeysFromArray(
                        formData.spaceDetails.filter(space => !!space.artifactId),
                        ["id"]
                    ),
                    deleted: omitKeysFromArray(
                        initialValues.spaceDetails.addedSpaces.filter(
                            feature =>
                                !formData.spaceDetails
                                    .map(values => values.artifactId)
                                    .includes(feature.artifactId)
                        ),
                        ["id"]
                    ),
                },
            });
        }

        if (onCreateProperty) {
            await createRealPropertyTrigger({
                businessUseCase: "SMART",
                clientName: alias,
                propertyDetails: props,
                featureDetails: [...formData.featureDetails],
                siteImprovementDetails: [...formData.siteImprovementDetails],
                spaceDetails: [...formData.spaceDetails],
            });
        }
    };

    return (
        <Column>
            <ApiIndicator
                isLoading={createIsLoading || updateIsLoading}
                isError={createIsError || updateIsError}
                error={createError || updateError}
                isToastError={true}
                loaderType={"linear"}
                timeout={10000}
            />
            <>{isPending ? "gathering data please be patient..." : null}</>
            {(initialValues || onCreateProperty) && (
                <Suspense
                    fallback={
                        <Box backgroundColor={"subdued"}>
                            <Loader size={"small"} type={"linear"} />
                        </Box>
                    }
                >
                    <LazyCreateEditPropertyForm
                        responsiveConfig={ResponsiveFormConfig}
                        forms={createPropertyFormSteps}
                        formName={"AddEditProperty"}
                        formInitialValues={initialValues ?? createPropertyInitialValues}
                        onFinalFormSubmit={handleSubmit}
                        errorToaster={responsiveSuccessToastHandler}
                    />
                </Suspense>
            )}
            <Space size={"m"} />
        </Column>
    );
};
